import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/features/appData";
import { apiGetCustomPropertiesForWorkspace, apiGetCustomPropertiesNotVibeForWorkspace } from "../../services/EmployeeRecordService";
import { CustomProperty } from "winslow-core";
import { Button, Icon } from "../../ds2/components";
import { RefreshCw, Copy, Download, Eye, EyeOff } from "lucide-react";
import "./CustomPropertiesJSON.css";

export const CustomPropertiesJSON = () => {
  const storeUser = useSelector(selectUser);
  const [customProperties, setCustomProperties] = useState<CustomProperty[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState<boolean>(false);

  useEffect(() => {
    if (storeUser?.currentWorkspaceId) {
      loadCustomProperties();
    }
  }, [storeUser?.currentWorkspaceId]);

  const loadCustomProperties = async () => {
    if (!storeUser?.currentWorkspaceId) return;

    try {
      setLoading(true);
      setError(null);
      const properties = await apiGetCustomPropertiesNotVibeForWorkspace(storeUser.currentWorkspaceId);
      setCustomProperties(properties || []);
    } catch (err) {
      console.error("Error loading custom properties:", err);
      setError("Failed to load custom properties");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadCustomProperties();
  };

  const handleCopyJSON = async () => {
    try {
      const jsonString = JSON.stringify(customProperties, null, 2);
      await navigator.clipboard.writeText(jsonString);
      // You could add a toast notification here
      console.log("JSON copied to clipboard");
    } catch (err) {
      console.error("Failed to copy JSON:", err);
    }
  };

  const handleDownloadJSON = () => {
    try {
      const jsonString = JSON.stringify(customProperties, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `custom-properties-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download JSON:", err);
    }
  };

  const formatCustomProperties = () => {
    return customProperties.map(prop => ({
      id: prop.id,
      label: prop.label,
      display: prop.display,
      type: prop.type,
      group: prop.group || null,
      description: prop.description || null,
      system: prop.system,
      required: prop.required,
      hidden: prop.hidden,
      isVibe: prop.isVibe,
      visibleRoles: prop.visibleRoles,
      visibleEmployeeTypes: prop.visibleEmployeeTypes,
      settings: prop.settings
    }));
  };

  if (!storeUser?.currentWorkspaceId) {
    return (
      <div className="custom-properties-json">
        <div className="custom-properties-json-error">
          <p>No workspace selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-properties-json">
      <div className="custom-properties-json-header">
        <div className="custom-properties-json-title">
          <h2>Custom Properties JSON</h2>
          <p className="custom-properties-json-subtitle">
            {customProperties.length} custom properties found
          </p>
        </div>
        
        <div className="custom-properties-json-actions">
          <Button
            variant="tertiary"
            size="md"
            leftIcon={<RefreshCw size={16} />}
            label="Refresh"
            onClick={handleRefresh}
            disabled={loading}
          />
          <Button
            variant="tertiary"
            size="md"
            leftIcon={showRaw ? <EyeOff size={16} /> : <Eye size={16} />}
            label={showRaw ? "Hide Raw" : "Show Raw"}
            onClick={() => setShowRaw(!showRaw)}
          />
          <Button
            variant="tertiary"
            size="md"
            leftIcon={<Copy size={16} />}
            label="Copy"
            onClick={handleCopyJSON}
            disabled={customProperties.length === 0}
          />
          <Button
            variant="tertiary"
            size="md"
            leftIcon={<Download size={16} />}
            label="Download"
            onClick={handleDownloadJSON}
            disabled={customProperties.length === 0}
          />
        </div>
      </div>

      <div className="custom-properties-json-content">
        {loading && (
          <div className="custom-properties-json-loading">
            <div className="custom-properties-json-spinner"></div>
            <p>Loading custom properties...</p>
          </div>
        )}

        {error && (
          <div className="custom-properties-json-error">
            <p>{error}</p>
            <Button
              variant="primary"
              size="md"
              label="Retry"
              onClick={handleRefresh}
            />
          </div>
        )}

        {!loading && !error && customProperties.length === 0 && (
          <div className="custom-properties-json-empty">
            <p>No custom properties found for this workspace</p>
            <Button
              variant="primary"
              size="md"
              label="Refresh"
              onClick={handleRefresh}
            />
          </div>
        )}

        {!loading && !error && customProperties.length > 0 && (
          <div className="custom-properties-json-display">
            <pre className="custom-properties-json-code">
              {JSON.stringify(
                showRaw ? customProperties : formatCustomProperties(),
                null,
                2
              )}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};
